import type { ResultOf } from '@graphql-typed-document-node/core'
import { GET_RELEASE_ENTRIES } from '@/queries'
import { normalizeSearchInput, ReleaseEntryFilterProps, ReleaseEntrySorts } from '@/utils'
import {
  OrderDirection,
  ReleaseEntryWhereInput,
  ReleaseItemOrderByInput,
  SeriesInfoTypeType,
} from '../../../../__generated__/graphql'
import { LineBuilderConfig, type PreviewEntryData } from '@/components/PreviewEntry.vue'
import { getVolumeNumbersFromVersions } from '@/stores/user-collection'
import { useUserListsStore } from '@/stores/user-lists'

const getCuratedListConditions = (filter: ReleaseEntryFilterProps): ReleaseEntryWhereInput[] => {
  const store = useUserListsStore()
  const conditions: ReleaseEntryWhereInput[] = []
  if (filter.selectedCuratedLists && filter.selectedCuratedLists.length > 0) {
    const ids = store.memberReleaseEntryIdsForLists(filter.selectedCuratedLists)
    conditions.push({ id: { in: ids } })
  }
  if (filter.excludedCuratedLists && filter.excludedCuratedLists.length > 0) {
    const ids = store.memberReleaseEntryIdsForLists(filter.excludedCuratedLists)
    if (ids.length > 0) conditions.push({ NOT: [{ id: { in: ids } }] })
  }
  return conditions
}

export const databaseLineBuilder: LineBuilderConfig = {
  preTitleLine: (item, { t }) => {
    return t('publisherLine', {
      publisher: item.publisherName,
    })
  },
  titleLine: (item) => item.localizedSeriesTitle,
  postTitleLine: (item, { t }) => {
    if (item.volumeCount !== undefined) {
      return t('releaseEntryVolumeAmountLineWithType', {
        count: item.totalVolumeCount,
        type: t(item.type),
      })
    }
    return ''
  },
}

export const getPaginationItemsForReleaseEntries = (
  data: ResultOf<typeof GET_RELEASE_ENTRIES>,
): PreviewEntryData[] =>
  data?.releaseEntries?.map((e) => ({
    localizedSeriesTitle: e.internalName,
    volumeCount:
      getVolumeNumbersFromVersions(e.sortedVolumeNumbers).length -
      getVolumeNumbersFromVersions(e.notReleasedVolumeNumbers).length,
    totalVolumeCount: getVolumeNumbersFromVersions(e.sortedVolumeNumbers).length,
    type: e.type as unknown as SeriesInfoTypeType,
    id: e.id,
    entryId: e.id,
    publisherName: e.publisherName,
    cover: e.cover,
    thumbHash: e.thumbHash,
    relationId: e.id,
  })) || []

export const getSortForReleaseEntries = (
  sorts: ReleaseEntrySorts,
): { orderBy: ReleaseItemOrderByInput[] } => {
  return {
    orderBy: [
      ...sorts
        .filter((s) => s.order != null && s.property !== 'orderNumber')
        .map((s) => ({
          [s.property]: s.order,
        })),
      {
        id: OrderDirection.Asc,
      },
    ],
  }
}

export const getWhereForReleaseEntries = (
  filter: ReleaseEntryFilterProps,
): { where: ReleaseEntryWhereInput } => {
  const publisherFilter: ReleaseEntryWhereInput[] = filter.selectedPublisher
    ? filter.selectedPublisher.map((publisher) => ({
        publisherName: { equals: publisher },
      }))
    : []
  const selectedMediumFilter: ReleaseEntryWhereInput[] = filter.selectedMedium
    ? filter.selectedMedium.map((medium) => ({ type: { equals: medium } }))
    : []
  const excludedPublisherFilter: ReleaseEntryWhereInput[] = filter.excludedPublisher
    ? filter.excludedPublisher.map((publisher) => ({
        publisherName: { equals: publisher },
      }))
    : []
  const excludedMediumFilter: ReleaseEntryWhereInput[] = filter.excludedMedium
    ? filter.excludedMedium.map((medium) => ({ type: { equals: medium } }))
    : []
  const tagsFilter: ReleaseEntryWhereInput[] = filter.selectedTags
    ? filter.selectedTags.map((tagId) => ({
        tagSearchField: { contains: `${tagId}` },
      }))
    : []
  const demographyFilter: ReleaseEntryWhereInput[] = filter.selectedDemographies
    ? filter.selectedDemographies.map((demographyId) => ({
        demographySearchField: { contains: `${demographyId}` },
      }))
    : []
  const excludedTagsFilter: ReleaseEntryWhereInput[] = filter.excludedTags
    ? filter.excludedTags.map((tagId) => ({
        tagSearchField: { contains: `${tagId}` },
      }))
    : []
  const excludedDemographyFilter: ReleaseEntryWhereInput[] = filter.excludedDemographies
    ? filter.excludedDemographies.map((demographyId) => ({
        demographySearchField: { contains: `${demographyId}` },
      }))
    : []
  const normalizedSearchInput = filter.seriesTitle
    ? normalizeSearchInput(filter.seriesTitle).map((part) => ({ titleSearch: { contains: part } }))
    : []
  const curatedListConditions = getCuratedListConditions(filter)
  const ANDConditions: ReleaseEntryWhereInput[] = [
    ...(publisherFilter.length > 0 ? [{ OR: publisherFilter }] : []),
    ...(selectedMediumFilter.length > 0 ? [{ OR: selectedMediumFilter }] : []),
    ...(excludedPublisherFilter.length > 0 ? [{ NOT: [{ OR: excludedPublisherFilter }] }] : []),
    ...(excludedMediumFilter.length > 0 ? [{ NOT: [{ OR: excludedMediumFilter }] }] : []),
    ...(tagsFilter.length > 0 ? [{ OR: tagsFilter }] : []),
    ...(demographyFilter.length > 0 ? [{ OR: demographyFilter }] : []),
    ...(excludedTagsFilter.length > 0 ? [{ NOT: [{ OR: excludedTagsFilter }] }] : []),
    ...(excludedDemographyFilter.length > 0 ? [{ NOT: [{ OR: excludedDemographyFilter }] }] : []),
    ...(filter.startDate ? [{ startDate: { gte: filter.startDate } }] : []),
    ...(filter.minScoreField ? [{ [filter.minScoreField]: { gt: '0.0' } }] : []),
    ...normalizedSearchInput,
    ...curatedListConditions,
  ]

  const ORConditions: ReleaseEntryWhereInput[] = []
  return {
    where: {
      ...(filter.excludeNoStartDate ? { startDate: { not: null } } : {}),
      ...(ANDConditions.length > 0 ? { AND: ANDConditions } : {}),
      ...(ORConditions.length > 0
        ? {
            OR: ORConditions,
          }
        : {}),
      disabled: { equals: false },
    },
  }
}
